import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { fromEvent } from 'rxjs';
import { SocketService } from '@core/services/socket.service';
import { ToastrService } from 'ngx-toastr';
import { PricingApiService } from './pricing-api.service';
import {
  GetPricesResponseData,
  PricingCategory,
  PricingItem,
  PricingOrder,
  PricingSort,
} from './pricing.types';

export type LoadType = 0 | 1; // 0 = approved prices, 1 = reviews

@Injectable()
export abstract class PaginatedPricingStore {
  protected readonly api = inject(PricingApiService);
  protected readonly socketService = inject(SocketService);
  protected readonly toastr = inject(ToastrService);
  protected readonly destroyRef = inject(DestroyRef);

  abstract readonly type: LoadType;
  protected abstract readonly errorMessage: string;
  protected abstract readonly defaultSort: PricingSort | null;
  protected abstract readonly defaultOrder: PricingOrder | null;

  readonly items = signal<PricingItem[]>([]);
  readonly total = signal(0);
  readonly pages = signal(1);
  readonly perPage = signal(100);
  readonly page = signal(1);
  readonly isLoading = signal(false);

  readonly search = signal<string | null>(null);
  readonly filter = signal<PricingCategory[] | null>(null);
  readonly sort = signal<PricingSort | null>(null);
  readonly order = signal<PricingOrder | null>(null);

  /**
   * Monotonically incremented on every loadItems call. Used to discard the
   * response (or error) from any in-flight request that the user has already
   * superseded with a newer one — fast pagination clicks would otherwise let
   * a stale response stomp the latest page's data and trigger spurious
   * toastr errors when older queued emits time out.
   */
  private loadSeq = 0;

  constructor() {
    fromEvent(this.socketService.socket, 'connect')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.loadItems());
  }

  async loadItems(attempt = 0): Promise<void> {
    const seq = ++this.loadSeq;
    this.isLoading.set(true);
    try {
      const res = await this.api.emit<GetPricesResponseData>('ap_get_prices', {
        // Lowercase at the emit boundary so case-insensitive search matches
        // server-stored names regardless of admin keystrokes; UI still shows
        // whatever the admin typed.
        search: this.search()?.toLowerCase() ?? null,
        filter: this.filter(),
        sort: this.sort(),
        order: this.order(),
        type: this.type,
        page: this.page(),
      });
      // A newer loadItems was issued while we were awaiting (e.g. user clicked
      // "next page" again before the previous response landed) — discard ours.
      if (seq !== this.loadSeq) return;
      this.items.set(res?.skins ?? []);
      this.total.set(res?.total ?? 0);
      this.pages.set(res?.pages ?? 1);
      this.perPage.set(res?.perPage ?? 100);
    } catch {
      // Stale-and-erroring: don't retry or surface a toastr — the newer load
      // is the canonical truth and will handle its own outcome.
      if (seq !== this.loadSeq) return;
      if (attempt < 2) return this.loadItems(attempt + 1);
      this.toastr.error(this.errorMessage);
    } finally {
      // Only clear the spinner when the LATEST load settles; older overlapping
      // requests that finish first leave it on for the still-in-flight one.
      if (seq === this.loadSeq) this.isLoading.set(false);
    }
  }

  setPage(page: number): void {
    this.page.set(page);
    void this.loadItems();
  }

  handleResetAll(): void {
    this.search.set(null);
    this.filter.set(null);
    this.sort.set(this.defaultSort);
    this.order.set(this.defaultOrder);
    this.page.set(1);
    void this.loadItems();
  }
}
